import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { runInAction, toJS } from 'mobx';
import { format } from 'date-fns';
import { useSnackbarContext } from '~/components/providers/SnackbarProvider';
import { useApp } from '~/hooks/useApp';
import {
  AmountInput,
  AmountRow,
  CheckboxInput,
  CheckboxLabel,
  CloseButton,
  DropdownList,
  FooterButton,
  FormField,
  FormLabel,
  ModalBody,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Textarea,
  WarningText,
} from './ModalChargeRefund.styled';

type ModalChargeRefundProps = {
  options: any;
  close: (value?: any) => void;
};

type ChargeType = 'free' | 'paid';
type ActionType = 'deposit' | 'refund';

const actionTypeOptions: { label: string; value: ActionType }[] = [
  { label: '충전', value: 'deposit' },
  { label: '환불', value: 'refund' },
];

const chargeTypeOptions: { label: string; value: ChargeType }[] = [
  { label: '무료', value: 'free' },
  { label: '유료', value: 'paid' },
];

const actionTypeLabel = { deposit: '충전', refund: '환불' };
const chargeTypeLabel = { free: '무료', paid: '유료' };

const getOptionLabel = (option: any) => {
  if (option?.[0]) {
    return option[0]?.label ? option[0].label : '';
  }

  return option ? option.label || '' : '';
};

const ModalChargeRefund = ({ options, close }: ModalChargeRefundProps) => {
  const app = useApp();
  const snackbar = useSnackbarContext();
  const [obj, setObj] = useState<any>({ clinicId: '', amount: '' });
  const [clinics, setClinics] = useState<any[]>([]);
  const [actionType, setActionType] = useState<ActionType | ''>('');
  const [chargeType, setChargeType] = useState<ChargeType | ''>('');
  const [isAll, setIsAll] = useState(false);
  const [reason, setReason] = useState('');
  const [memo, setMemo] = useState('');

  useEffect(() => {
    const copyClinics =
      options.clinics?.map((item: any) => ({
        ...item,
        id: item.id,
        label: `${item.name} (${item.id})`,
        value: item.id,
      })) || [];

    setClinics(copyClinics);
  }, [options.clinics]);

  const setMemoContents = (clinicMemo: any, memo: string) => {
    let parsedMemo = [];

    try {
      parsedMemo = JSON.parse(clinicMemo || '[]') || [];
    } catch (error) {
      parsedMemo = [];
    }

    const currentMemo = {
      content: memo || '',
      timestamp: format(new Date(), 'yy-MM-dd'),
    };

    return JSON.stringify(parsedMemo.concat(currentMemo));
  };

  const chargeRefundCallApi = useCallback(
    async (
      currentObj: any,
      clinic: any,
      currentReason: string,
      currentMemo: string,
      currentChargeType: ChargeType,
      currentActionType: ActionType,
      currentIsAll: boolean
    ) => {
      try {
        const { id, memo: clinicMemo } = clinic;
        const payload = {
          clinicId: id,
          amount: currentIsAll ? 0 : Number(currentObj.amount),
          status: 'complete',
          requestType: currentActionType,
          type: currentChargeType,
          reason: currentReason,
          memo: currentMemo,
          clinicMemo: setMemoContents(clinicMemo, currentMemo),
          isAll: currentIsAll,
        };

        await app.$smsRepo.sms.create(payload);
        snackbar.success('작업을 완료했습니다.');
        close();
      } catch (e: any) {
        snackbar.alert(e.description);
      }
    },
    [app.$smsRepo.sms, close, snackbar]
  );

  const onClickSave = () => {
    if (!obj.clinicId) {
      snackbar.alert('병원을 선택해주세요.');
      return;
    }

    if (!actionType) {
      snackbar.alert('처리유형을 선택해주세요.');
      return;
    }

    if (!chargeType) {
      snackbar.alert('유/무료 구분을 선택해주세요.');
      return;
    }

    if (!obj.amount && !isAll) {
      snackbar.alert('금액을 입력해주세요.');
      return;
    }

    if (!reason) {
      snackbar.alert('사유를 입력해주세요.');
      return;
    }

    const findClinics = toJS(options.clinics).find(
      (v: any) => v.id === Number(obj.clinicId)
    );
    const clinic = { ...toJS(findClinics) };

    runInAction(() => {
      app.$modal
        .basic({
          body:
            `상호(병원명): ${clinic.name} <br/>` +
            `${chargeTypeLabel[chargeType]} ${actionTypeLabel[actionType]}<br/>` +
            `금액: ${isAll ? '전체금액' : obj.amount} <br/>` +
            `진행을 하시겠습니까?`,
          buttons: [
            {
              text: 'CANCEL',
              class: 'btn-default',
            },
            {
              text: 'CONFIRM',
              class: 'btn-primary',
            },
          ],
        })
        .then((selectedIdx) => {
          if (selectedIdx === 1) {
            chargeRefundCallApi(
              obj,
              clinic,
              reason,
              memo,
              chargeType,
              actionType,
              isAll
            ).then(() => {
              app.sleep(500);
              options.callApi();
            });
          }
        });
    });
  };

  const onChangeActionType = (option: { value: ActionType } | null) => {
    const nextActionType = option?.value || '';
    setActionType(nextActionType);
    setIsAll(false);
  };

  const onKeyDownAmount = (e: any) => {
    if (e.key === '.' || e.key === '-' || e.key === 'e') {
      e.preventDefault();
    }
  };

  return (
    <ModalContainer>
      <ModalHeader>
        <ModalTitle>{'충전/환불 관리'}</ModalTitle>
        <CloseButton onClick={() => close()} className="zmdi zmdi-close" />
      </ModalHeader>

      <ModalBody>
        <WarningText>
          {
            '무료 충전 후에는 회수 불가합니다. 이전 신청 내역 확인 후 실행해주세요.'
          }
        </WarningText>

        <FormField>
          <FormLabel>{'상호 (병원명)'}</FormLabel>
          <DropdownList
            value={clinics.filter((option) => obj.clinicId === option.id)}
            options={clinics}
            onChange={(option: any) => {
              setObj({ ...obj, clinicId: option?.value || '' });
            }}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option: any, value: any) =>
              option?.value === value?.value
            }
            placeholder="병원을 선택해주세요."
          />
        </FormField>

        <FormField>
          <FormLabel>{'처리유형'}</FormLabel>
          <DropdownList
            value={actionTypeOptions.filter(
              (option) => option.value === actionType
            )}
            options={actionTypeOptions}
            onChange={onChangeActionType}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option: any, value: any) =>
              option?.value === value?.value
            }
            placeholder="처리유형을 선택해주세요."
          />
        </FormField>

        <FormField>
          <FormLabel>{'유/무료 구분'}</FormLabel>
          <DropdownList
            value={chargeTypeOptions.filter(
              (option) => option.value === chargeType
            )}
            options={chargeTypeOptions}
            onChange={(option: { value: ChargeType } | null) => {
              setChargeType(option?.value || '');
            }}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option: any, value: any) =>
              option?.value === value?.value
            }
            placeholder="유형을 선택해주세요."
          />
        </FormField>

        <FormField>
          <FormLabel>{'금액'}</FormLabel>
          <AmountRow>
            <AmountInput
              className={actionType === 'refund' ? 'refund' : ''}
              name="amount"
              type="number"
              min={1}
              step={1000}
              value={obj.amount}
              disabled={isAll}
              onKeyDown={onKeyDownAmount}
              onChange={(e) => {
                setObj({ ...obj, [e.target.name]: e.target.value });
              }}
            />
            {actionType === 'refund' && (
              <CheckboxLabel>
                <CheckboxInput
                  type="checkbox"
                  checked={isAll}
                  onChange={(e) => {
                    setIsAll(e.target.checked);
                    if (e.target.checked) {
                      setObj({ ...obj, amount: '' });
                    }
                  }}
                />
                {'전체금액'}
              </CheckboxLabel>
            )}
          </AmountRow>
        </FormField>

        <FormField>
          <FormLabel>{'사유'}</FormLabel>
          <Textarea
            value={reason}
            placeholder="사유를 입력하세요."
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>

        <FormField>
          <FormLabel>{'CS 메모'}</FormLabel>
          <Textarea
            value={memo}
            placeholder="사유를 입력하세요."
            onChange={(e) => setMemo(e.target.value)}
          />
        </FormField>
      </ModalBody>

      <ModalFooter>
        <FooterButton onClick={() => close(0)}>
          {app.$translate('CANCEL')}
        </FooterButton>
        <FooterButton className="primary" onClick={onClickSave}>
          {app.$translate('SAVE')}
        </FooterButton>
      </ModalFooter>
    </ModalContainer>
  );
};

export default observer(ModalChargeRefund);
